import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Flex,
  Grid,
  GridItem,
  Link,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { AiOutlineFile } from "react-icons/ai";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "wouter";

import {
  FilesSchema,
  getScenarioFileForDownload,
  useScenario,
  useScenarioReview,
} from "@nens/ldo/api-v1";
import {
  ActionButtons,
  ErrorBox,
  QualityControl,
  ScenarioExternalProcessings,
  ScenarioHistory,
  ScenarioRastersMap,
  ScenarioStatus,
} from "@nens/ldo/components";
import { Breadcrumbs, MetadataTable } from "@nens/ldo/components";
import { useActionAllowed } from "@nens/ldo/hooks";
import { removeNullUndefined } from "@nens/shared-utils";

interface ScenarioDetailPageProps {
  scenarioIdParam: string;
}

function DownloadButton({ scenarioId, filename }: { scenarioId: number; filename: string }) {
  const fileForDownload = useQuery(getScenarioFileForDownload(scenarioId, filename));

  return (
    <Link href={fileForDownload.data?.url} textDecoration="underline">
      {filename}
    </Link>
  );
}

function Downloads({ scenarioId, files }: { scenarioId: number; files: FilesSchema }) {
  const humanSize = (bytes: number) => {
    if (bytes === 0) {
      return "0.00 B";
    }
    const e = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, e)).toFixed(2) + " " + " KMGTP".charAt(e) + "B";
  };
  const downloads = removeNullUndefined(
    Object.entries(files).map(([filename, file]) =>
      file.file === null
        ? null
        : {
            filename,
            type: file.type,
            size: file.file.size,
          }
    )
  );

  return (
    <Box rounded="md" border="1px solid #ccc">
      <Flex
        borderBottom="1px solid #ccc"
        py="10px"
        px="14px"
        justifyItems={"flex-start"}
        alignItems={"center"}
      >
        <Text fontWeight={"semibold"}>
          <FormattedMessage id="detailpage.downloads" defaultMessage="Downloads" />
        </Text>
        <Box
          color="bij12_1.700"
          fontWeight="semibold"
          bgColor={"#CC614D20"}
          fontSize={"sm"}
          rounded="full"
          px="14px"
          py="1px"
          ml="12px"
        >
          {downloads.length.toString() || "0"}
        </Box>
      </Flex>
      {downloads.length === 0 ? (
        <Flex justifyContent="center" alignItems="center">
          <Box py="20">
            <Text>
              <FormattedMessage
                id="detailpage.downloads.no_downloads_available"
                defaultMessage="No downloads available"
              />
            </Text>
          </Box>
        </Flex>
      ) : (
        <List spacing={3} p="18px">
          {downloads.map((download: { filename: string; size: number }) => {
            return (
              <ListItem cursor={"pointer"} key={download.filename}>
                <Flex justifyContent="space-between">
                  <Flex alignItems="center">
                    <ListIcon as={AiOutlineFile} color="bij12_1.700" />
                    <DownloadButton scenarioId={scenarioId} filename={download.filename} />
                  </Flex>
                  <Text>{humanSize(Number(download.size))}</Text>
                </Flex>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}

export default function ScenarioDetailPage({ scenarioIdParam }: ScenarioDetailPageProps) {
  const setLocation = useLocation()[1];

  const intl = useIntl();
  const scenarioId = parseInt(scenarioIdParam);
  const scenarioResponse = useScenario(scenarioId);
  const scenarioReview = useScenarioReview(scenarioId);

  const scenario = scenarioResponse.data ?? null;
  const status = scenario?.status ?? null;

  const actionAllowed = useActionAllowed(scenario);

  if (Number.isNaN(scenarioId) || scenarioResponse.isError) {
    setLocation("/scenarios");
  }

  const showQualityControl =
    actionAllowed("view qc status") && (scenarioReview.isSuccess || scenarioReview.isError);
  const editQualityControl = actionAllowed("qc accept/reject");

  const errors = (scenario?.errors ?? []).map((error) => error.msg);

  return (
    <Box w="100%">
      <Breadcrumbs
        crumbs={[
          { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
          {
            label: intl.formatMessage({ id: "pages.scenarios", defaultMessage: "Scenarios" }),
            href: "/scenarios",
          },
          {
            label: scenarioResponse.isSuccess
              ? scenarioResponse.data.name
              : intl.formatMessage({ id: "pages.scenario", defaultMessage: "Scenario" }),
            href: `/scenarios/${scenarioId}`,
          },
        ]}
      />

      <Container maxW={"7xl"} py={"24px"}>
        <Flex justifyContent="space-between">
          <ButtonGroup variant="outline">
            <Button
              onClick={() => setLocation("/scenarios")}
              title={intl.formatMessage({
                id: "detailpage.back_to_scenarios",
                defaultMessage: "Back to scenarios",
              })}
              leftIcon={<ArrowBackIcon />}
            >
              <Box
                display={{
                  base: "none",
                  sm: "block",
                }}
              >
                <FormattedMessage
                  id="detailpage.back_to_scenarios"
                  defaultMessage="Back to scenarios"
                />
              </Box>
            </Button>
          </ButtonGroup>
          {scenario ? <ActionButtons scenario={scenario} /> : null}
        </Flex>

        <Box rounded="md" w="full" border="1px solid #ccc" my="40px">
          <Grid w="full" templateRows={"repeat(2, 1fr)"} templateColumns={"repeat(5, 1fr)"} gap={0}>
            <GridItem colSpan={5} roundedTop="xl" borderBottom="1px solid #ccc">
              <Box p="14px">
                {scenarioResponse.isSuccess ? (
                  <Text fontWeight="semibold" fontSize="2xl">
                    {scenarioResponse.data.name}{" "}
                    <span style={{ color: "#ccc" }}>({scenarioResponse.data.id})</span>
                  </Text>
                ) : (
                  <Text fontWeight="semibold" fontSize="2xl" color="gray.300">
                    <FormattedMessage id="misc.loading" defaultMessage="Loading..." />
                  </Text>
                )}
                <Text fontWeight="normal" fontSize="sm" py="4px">
                  {scenarioResponse?.data?.createdAt.toLocaleDateString("nl-NL")}
                </Text>
              </Box>
            </GridItem>
            <GridItem colSpan={1} roundedBottomLeft="xl" borderRight="1px solid #ccc">
              <Flex flexDirection="column" alignItems={"flex-start"} px="12px" py={"4px"}>
                <Button variant="ghost" pl="6px" disabled>
                  Status
                </Button>
                <ScenarioStatus status={status ?? undefined} />
              </Flex>
            </GridItem>
            <GridItem colSpan={1} borderRight="1px solid #ccc">
              {showQualityControl && scenario ? (
                <QualityControl
                  scenario={scenario}
                  editable={editQualityControl}
                  reviewObject={scenarioReview.isSuccess ? scenarioReview.data : null}
                />
              ) : null}
            </GridItem>
            <GridItem colSpan={2} roundedBottomRight="xl"></GridItem>
          </Grid>
        </Box>

        {errors.length >= 0 ? <ErrorBox errors={errors} /> : null}

        <SimpleGrid
          columns={{
            base: 1,
            lg: 2,
          }}
          spacing={10}
          w="full"
          my="40px"
        >
          <Downloads scenarioId={scenarioId} files={scenario?.files ?? {}} />
          <Flex
            rounded="md"
            border="1px solid #ccc"
            justifyContent="center"
            alignItems="center"
            h="400px"
          >
            {scenarioResponse?.data ? (
              <Box
                w="full"
                h="400px"
                flexShrink="0"
                rounded="lg"
                bgColor={"white"}
                position="relative"
              >
                <ScenarioRastersMap
                  size="s"
                  scenario={scenarioResponse.data}
                  mapId={`preview-map-${scenarioResponse.data.id}`}
                  extraButtons={[
                    <Button
                      size="md"
                      backgroundColor="white"
                      variant="outline"
                      px="20px"
                      rounded="md"
                      pointerEvents="auto"
                      onClick={() => setLocation(`/scenarios/${scenarioResponse.data.id}/map`)}
                    >
                      <FormattedMessage id="map.open_large" defaultMessage="Open large map" />
                    </Button>,
                  ]}
                />
              </Box>
            ) : null}
          </Flex>
        </SimpleGrid>

        {scenario ? <MetadataTable scenario={scenario} /> : null}
        {scenario ? <ScenarioExternalProcessings scenario={scenario} /> : null}
        {scenario ? <ScenarioHistory scenario={scenario} /> : null}
      </Container>
    </Box>
  );
}
